import { useCallback, useEffect, useState } from "react";
import { LoaderCircle } from "lucide-react";
import { api, ApiError } from "./api";
import { AppShell, type AppView } from "./components/AppShell";
import { Login } from "./components/Login";
import { Builder } from "./components/Builder";
import { WorkflowsView } from "./components/WorkflowsView";
import { AgentsView } from "./components/AgentsView";
import { RunsView } from "./components/RunsView";
import { SettingsView } from "./components/SettingsView";
import { ChatView } from "./components/ChatView";
import type { SessionUser, WorkflowSummary } from "../shared/types";

export default function App() {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<AppView>("chat");
  const [workflows, setWorkflows] = useState<WorkflowSummary[]>([]);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(null);

  const refreshWorkflows = useCallback(async () => {
    const result = await api.workflows();
    setWorkflows(result.workflows);
    setSelectedWorkflowId((current) => {
      if (current && result.workflows.some((workflow) => workflow.id === current)) return current;
      return result.workflows[0]?.id ?? null;
    });
    return result.workflows;
  }, []);

  useEffect(() => {
    api.me()
      .then(async ({ user: sessionUser }) => {
        setUser(sessionUser);
        await refreshWorkflows();
      })
      .catch((error) => {
        if (!(error instanceof ApiError) || error.status !== 401) console.error(error);
      })
      .finally(() => setLoading(false));
  }, [refreshWorkflows]);

  async function login(email: string, password: string) {
    const result = await api.login(email, password);
    setUser(result.user);
    await refreshWorkflows();
  }

  async function logout() {
    await api.logout();
    setUser(null);
    setWorkflows([]);
    setSelectedWorkflowId(null);
  }

  function openWorkflow(id: string) {
    setSelectedWorkflowId(id);
    setView("builder");
  }

  function updateWorkflow(updated: WorkflowSummary) {
    setWorkflows((current) =>
      current.map((workflow) => (workflow.id === updated.id ? updated : workflow)),
    );
  }

  if (loading) {
    return <div className="boot-screen"><LoaderCircle className="spin" size={22} />Carregando workspace</div>;
  }

  if (!user) return <Login onLogin={login} />;

  const selectedWorkflow = workflows.find((workflow) => workflow.id === selectedWorkflowId) ?? null;

  return (
    <AppShell user={user} view={view} onViewChange={setView} onLogout={logout}>
      {view === "chat" && <ChatView />}
      {view === "builder" && selectedWorkflow && (
        <Builder workflow={selectedWorkflow} onWorkflowUpdate={updateWorkflow} />
      )}
      {view === "builder" && !selectedWorkflow && (
        <div className="empty-page"><h1>Nenhum workflow</h1><p>Crie o primeiro fluxo para abrir o editor.</p></div>
      )}
      {view === "workflows" && (
        <WorkflowsView
          workflows={workflows}
          onOpen={openWorkflow}
          onRefresh={refreshWorkflows}
        />
      )}
      {view === "agents" && <AgentsView />}
      {view === "runs" && <RunsView onOpenWorkflow={openWorkflow} />}
      {view === "settings" && <SettingsView user={user} />}
    </AppShell>
  );
}
